import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import { credit } from "../services/WalletService";
import Seo from "../Components/Seo";

const REWARD_PER_REFERRAL = 50;
const FRIEND_REWARD = 25;

export default function ShareAndEarn() {
  const navigate = useNavigate();
  const [mobileNo, setMobileNo] = useState("");
  const [code, setCode] = useState("");
  const [totalReferred, setTotalReferred] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [msg, setMsg] = useState("");

  const fetchReferralData = useCallback(async (mobile) => {
    try {
      const res = await fetch(`${API_BASE_URL}/referral/my`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ MobileNo: mobile }),
      });
      if (res.ok) {
        const data = await res.json();
        setCode(data.code || "");
        setTotalReferred(data.totalReferred || 0);
        setTotalEarned(data.totalEarned || 0);
        setPendingAmount(data.pendingAmount || 0);
        setReferrals(Array.isArray(data.referrals) ? data.referrals : []);
      }
    } catch (e) {
      console.warn("referral fetch failed:", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mobile = localStorage.getItem("userMobileNumber") || "";
    if (!mobile) {
      navigate("/login");
      return;
    }
    setMobileNo(mobile);
    fetchReferralData(mobile);
  }, [navigate, fetchReferralData]);

  const handleCopyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setMsg("Code copied!");
      setTimeout(() => setMsg(""), 2000);
    } catch {
      setMsg("Copy failed — manually copy karein");
      setTimeout(() => setMsg(""), 2500);
    }
  };

  const handleShare = async () => {
    if (!code) return;
    const message = `Hey! Join AIO INDIA and get ₹${FRIEND_REWARD} bonus!\n\nUse my referral code: ${code}\n\nWebsite: https://www.facebookindia.in`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Share AIO INDIA", text: message });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(message);
      setMsg("Message copied — paste karke share karein!");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setMsg("Share failed");
      setTimeout(() => setMsg(""), 2000);
    }
  };

  const handleWithdraw = async () => {
    if (pendingAmount <= 0) return;
    setWithdrawing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/referral/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ MobileNo: mobileNo }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const amount = data.amount || 0;
        if (amount > 0) {
          await credit(amount, "Share & Earn Referral Reward");
          setMsg(`₹${amount} wallet mein add ho gaya!`);
          setPendingAmount(0);
          fetchReferralData(mobileNo);
        } else {
          setMsg("Koi pending reward nahi hai");
        }
      } else {
        setMsg(data?.message || "Withdrawal failed");
      }
    } catch {
      setMsg("Network error. Try again.");
    } finally {
      setWithdrawing(false);
      setTimeout(() => setMsg(""), 3000);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px", color: "#94a3b8", fontWeight: 600 }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa", paddingBottom: "40px" }}>
      <Seo
        title="Share & Earn — Referral Rewards"
        description="AIO India referral program — apne friends ko invite karein, har referral pe ₹50 kamayein. Friend ko bhi ₹25 bonus."
        path="/share-earn"
      />

      <div
        style={{
          background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
          padding: "30px 20px 44px",
          textAlign: "center",
          borderRadius: "0 0 24px 24px",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: "40px" }}>🎁</div>
        <h1 style={{ margin: "10px 0 4px", fontSize: "24px", fontWeight: 800 }}>Share & Earn</h1>
        <div style={{ fontSize: "14px", opacity: 0.85 }}>Invite friends, earn rewards!</div>
      </div>

      <div
        style={{
          maxWidth: "560px", margin: "-24px auto 0", background: "#fff",
          borderRadius: "16px", padding: "20px", textAlign: "center",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)", position: "relative", zIndex: 2,
          marginLeft: "auto", marginRight: "auto",
        }}
      >
        <div style={{ fontSize: "13px", color: "#64748b", fontWeight: 600 }}>Your Referral Code</div>
        <div
          style={{
            fontSize: "32px", fontWeight: 900, color: "#4f46e5", letterSpacing: "4px",
            margin: "8px 0 16px",
          }}
        >
          {code || "------"}
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button onClick={handleCopyCode} style={btnCopy}>
            📋 Copy Code
          </button>
          <button onClick={handleShare} style={btnShare}>
            📤 Share
          </button>
        </div>
        {msg && (
          <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: 700, color: "#16a34a" }}>{msg}</div>
        )}
      </div>

      <div style={{ maxWidth: "560px", margin: "16px auto 0", display: "flex", gap: "12px", padding: "0 16px" }}>
        <div style={statCard}>
          <div style={statValue}>{totalReferred}</div>
          <div style={statLabel}>Friends Joined</div>
        </div>
        <div style={statCard}>
          <div style={statValue}>₹{totalEarned}</div>
          <div style={statLabel}>Total Earned</div>
        </div>
      </div>

      {pendingAmount > 0 && (
        <div style={{ maxWidth: "560px", margin: "16px auto 0", padding: "0 16px" }}>
          <button
            onClick={handleWithdraw}
            disabled={withdrawing}
            style={{
              width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "linear-gradient(135deg,#16a34a,#22c55e)", border: "none",
              borderRadius: "16px", padding: "18px", cursor: "pointer", color: "#fff",
              boxShadow: "0 6px 18px rgba(22,163,74,0.35)", opacity: withdrawing ? 0.7 : 1,
            }}
          >
            <span style={{ textAlign: "left" }}>
              <div style={{ fontSize: "13px", opacity: 0.85 }}>Pending Rewards</div>
              <div style={{ fontSize: "26px", fontWeight: 900 }}>₹{pendingAmount}</div>
            </span>
            <span
              style={{
                background: "rgba(255,255,255,0.25)", padding: "10px 14px",
                borderRadius: "10px", fontWeight: 700, fontSize: "14px",
              }}
            >
              {withdrawing ? "Claiming..." : "Claim to Wallet 💰"}
            </span>
          </button>
        </div>
      )}

      <div style={{ ...card, maxWidth: "560px", margin: "16px auto 0" }}>
        <h3 style={cardTitle}>How it works</h3>
        {[
          "Share your referral code with friends",
          "Friend website pe signup kare aur code dale",
          `Aapko ₹${REWARD_PER_REFERRAL} milenge, friend ko ₹${FRIEND_REWARD}`,
        ].map((text, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <span
              style={{
                width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
                background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: "13px", fontWeight: 800,
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontSize: "14px", color: "#0f172a", lineHeight: 1.5 }}>{text}</span>
          </div>
        ))}
      </div>

      {referrals.length > 0 && (
        <div style={{ ...card, maxWidth: "560px", margin: "16px auto 0" }}>
          <h3 style={cardTitle}>Your Referrals</h3>
          {referrals.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", fontWeight: 600 }}>
                👤 {item.referredMobile ? `****${String(item.referredMobile).slice(-4)}` : "---"}
              </span>
              <span style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, color: item.status === 1 ? "#64748b" : "#16a34a" }}>
                  +₹{item.reward}
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                  {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                </div>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnCopy = {
  flex: 1, padding: "12px", borderRadius: "10px", border: "1.5px solid #4f46e5",
  background: "#eef2ff", color: "#4f46e5", fontWeight: 700, fontSize: "14px", cursor: "pointer",
};
const btnShare = {
  flex: 1, padding: "12px", borderRadius: "10px", border: "none",
  background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
  fontWeight: 700, fontSize: "14px", cursor: "pointer",
  boxShadow: "0 4px 12px rgba(79,70,229,0.35)",
};
const statCard = {
  flex: 1, background: "#fff", borderRadius: "16px", padding: "16px",
  textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
};
const statValue = { fontSize: "22px", fontWeight: 800, color: "#0f172a" };
const statLabel = { fontSize: "12px", color: "#64748b", marginTop: "4px", fontWeight: 500 };
const card = {
  background: "#fff", borderRadius: "16px", padding: "18px",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
};
const cardTitle = { margin: "0 0 14px", fontSize: "16px", fontWeight: 700, color: "#0f172a" };
